import {
  ConfirmModal,
  DetailTitleComponent,
  CustomButton,
  CustomTabs,
} from '@/shared/ui/components';
import { zeroPad } from '@/shared/lib';
import classNames from 'classnames';
import { t } from 'ttag';
import { useRmaDetails } from './useRmaDetails';
import NewTicket from './newTicket';
import TicketInbound from './ticketInbound';
import TicketOutbound from './ticketOutbound/ticketOutbound';

/**
 * Details page for a single RMA ticket, switching between editing and read-only tracking views. Draws a detail title bar with a back link to /rma fed by generateTitleBlockArray and generateTitleRightComponent, and once loaded renders either the editable NewTicket form with a cancel-request ConfirmModal wired to handleCancelRmaTicket for a New ticket, or the Outbound and Inbound CustomTabs from listRMADetailTab driven by state.activeTab and navigateTab.
 *
 * @returns Page element for the RMA ticket details.
 */
const RmaDetails = () => {
  const {
    state,
    setState,
    newTicketRef,
    navigate,
    handleCancelRmaTicket,
    validateUpdateData,
    updateHasChangedData,
    navigateTab,
    handleRefreshTracking,
  } = useRmaDetails();

  /**
   * Builds the title bar summary blocks for an RMA ticket. Returns a pair of entries holding the zero-padded RMA id and the facility name when state.rmaTicket is loaded, and null otherwise.
   *
   * @returns Title and content pairs for the RMA header, or null before the ticket loads.
   */
  const generateTitleBlockArray = () => {
    if (state.rmaTicket) {
      const { rmaId, facility } = state.rmaTicket;
      const titleBlockArray = [
        {
          title: t`RMA ID`,
          content: zeroPad(rmaId),
        },
        {
          title: t`Facility`,
          content: facility.name,
        },
      ];
      return titleBlockArray;
    }
    return undefined;
  };

  /**
   * Builds the right-hand header content of the RMA details page from the ticket status. Returns null until state.isLoadedData, then for a New ticket renders Cancel Request opening the confirmation modal plus a Save button enabled only when hasChangedData and calling validateUpdateData, for Approved an Approved label, and for Rejected a Canceled label.
   *
   * @returns Header action buttons or status label element, or null when unavailable.
   */
  const generateTitleRightComponent = () => {
    if (state.isLoadedData) {
      const { status } = state.rmaTicket || {};
      switch (status) {
        case 'New': {
          return (
            <div className="header-right-btn">
              <CustomButton
                id="cancel-request-btn"
                onClick={() => {
                  setState({ isOpenCancelRequestModal: true });
                }}
                style="danger"
                type="outline"
                size="small"
              >
                {t`Cancel Request`}
              </CustomButton>
              <CustomButton
                id="save-btn"
                disabled={!state.hasChangedData}
                onClick={validateUpdateData}
                size="small"
              >
                {t`Save`}
              </CustomButton>
            </div>
          );
        }
        case 'Approved': {
          return <div className="header-right-label --approved">{t`Approved`}</div>;
        }
        case 'Rejected': {
          return <div className="header-right-label --canceled">{t`Canceled`}</div>;
        }
        default: {
          return null;
        }
      }
    }
    return null;
  };

  /**
   * Builds the Outbound and Inbound tab descriptors of the RMA details page. Returns two entries rendering TicketOutbound and TicketInbound, each fed the loaded RMA ticket, the Canada and US state and country lists, the renewal stock flag and handleRefreshTracking.
   *
   * @returns Tab descriptors for the outbound and inbound RMA views.
   */
  const listRMADetailTab = () => [
    {
      key: '1',
      label: <div>{t`Outbound`}</div>,
      children: (
        <TicketOutbound
          data={state.rmaTicket}
          listStatesCA={state.listStatesCA}
          listStatesUS={state.listStatesUS}
          listCountries={state.listCountries}
          hasRenewalStock={state.hasRenewalStock}
          handleRefreshTracking={handleRefreshTracking}
        />
      ),
    },
    {
      key: '2',
      label: <div>{t`Inbound`}</div>,
      children: (
        <TicketInbound
          data={state.rmaTicket}
          listStatesCA={state.listStatesCA}
          listStatesUS={state.listStatesUS}
          listCountries={state.listCountries}
          hasRenewalStock={state.hasRenewalStock}
          handleRefreshTracking={handleRefreshTracking}
        />
      ),
    },
  ];

  return (
    <div className="custom-page rma-detail">
      <div
        className={classNames('top-container', {
          '--has-box-shadow': state.isScroll,
        })}
      >
        <DetailTitleComponent
          isLoadedData={state.isLoadedData}
          handleClickBack={() => {
            navigate('/rma');
          }}
          title={t`RMA`}
          titleBlockArray={generateTitleBlockArray()}
          isEditReportPage
          rightComponent={generateTitleRightComponent()}
        />
      </div>

      {state.isLoadedData &&
        (state.rmaTicket?.status === 'New' ? (
          <div className="custom-body rma-detail-body">
            <ConfirmModal
              isOpen={state.isOpenCancelRequestModal}
              onClickLeftButton={() => {
                setState({ isOpenCancelRequestModal: false });
              }}
              onClickRightButton={handleCancelRmaTicket}
              leftButtonName={t`Back`}
              rightButtonName={t`Cancel`}
              title={t`Cancel Request`}
              question={t`Are you sure you want to cancel this RMA request?`}
              isSaving={state.isLoadingModal}
              buttonRightType="outline"
              buttonRightStyle="danger"
            />
            <NewTicket
              ref={newTicketRef}
              data={state.rmaTicket}
              updateHasChangedData={updateHasChangedData}
              countries={state.listCountries}
              hasRenewalStock={state.hasRenewalStock}
            />
          </div>
        ) : (
          <CustomTabs
            className="rma-detail-tab"
            tabTitleClassName="tab-title"
            activeKey={state.activeTab}
            onChange={navigateTab}
            items={listRMADetailTab() as any}
          />
        ))}
    </div>
  );
};

export default RmaDetails;
